import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

import { getPayerBaseUrl } from '../utils/URLBuilder';

/**
 * Data source for Ops → Terminals: the terminals/{SN} fleet registry, live,
 * newest activity first. Written server-side only (config sync, kiosk arms,
 * reader webhooks) — this model reads and carries the support INTENTS; all
 * display derivation stays in the screen.
 *
 * Shape (house convention): [loading, terminals, error, intents].
 */
export function useTerminals() {
    const [state, setState] = useState({ loading: true, terminals: [], error: null });

    useEffect(() => firebase.firestore()
        .collection('terminals')
        .orderBy('updatedAt', 'desc')
        .limit(200)
        .onSnapshot(
            (snap) => setState({
                loading: false,
                terminals: snap.docs.map((doc) => ({ sn: doc.id, ...doc.data() })),
                error: null,
            }),
            (err) => {
                console.warn('[useTerminals] listen failed:', err && err.message);
                setState({ loading: false, terminals: [], error: err.message || 'Could not load terminals' });
            },
        ), []);

    /**
     * Support intent: arm (or restart) the standing kiosk screen on one
     * device. Resolves to the payer server's outcome string — 'armed' is
     * success; anything else names the guard that refused ('not-kiosk',
     * 'outside-hours', 'locked', ...).
     */
    const startKiosk = async (terminal) => {
        // Strike-admin-only server side — the token is how it knows who asks.
        const idToken = await firebase.auth().currentUser.getIdToken();
        const res = await fetch(
            `${getPayerBaseUrl()}/api/pos/tags/${terminal.deviceTagId}/kiosk-arm`,
            { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } },
        );
        const json = await res.json().catch(() => ({}));
        // The server names the guard that refused AND, on arm-failed, the
        // reader's actual reason — carry both so the screen never has to
        // invent an explanation (the 2026-10-02 incident wore the wrong one).
        return {
            outcome: json.outcome || `http-${res.status}`,
            reason: json.reason || null,
        };
    };

    /**
     * Support intent: sweep the WHOLE fleet now — the cron's sweep on demand
     * (Charles, 15 Oct 2026: after a deploy, screens lost to the 503 window
     * waited for the next tick). Resolves to { swept, counts, results }, or
     * { error } on an http failure. Strike-admin-only server side.
     */
    const sweepAll = async () => {
        const idToken = await firebase.auth().currentUser.getIdToken();
        const res = await fetch(
            `${getPayerBaseUrl()}/api/pos/kiosk-sweep-all`,
            { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return { error: json.error || `http-${res.status}` };
        return { swept: json.swept || 0, counts: json.counts || {}, results: json.results || [] };
    };

    return [state.loading, state.terminals, state.error, { startKiosk, sweepAll }];
}

/** ONE terminal's registry doc, live: [loading, terminal, error]. terminal
 * is null while loading and when no doc exists. */
export function useTerminal(serialNumber) {
    const [state, setState] = useState({ loading: true, terminal: null, error: null });

    useEffect(() => {
        if (!serialNumber) { setState({ loading: false, terminal: null, error: null }); return undefined; }
        setState({ loading: true, terminal: null, error: null });
        return firebase.firestore()
            .collection('terminals').doc(serialNumber)
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    terminal: snap.exists ? { sn: snap.id, ...snap.data() } : null,
                    error: null,
                }),
                (err) => {
                    console.warn('[useTerminal] listen failed:', err && err.message);
                    setState({ loading: false, terminal: null, error: err.message || 'Could not load the terminal' });
                },
            );
    }, [serialNumber]);

    return [state.loading, state.terminal, state.error];
}

/** One terminal's history, newest first, live: [loading, events, error]. */
export function useTerminalHistory(serialNumber, max = 50) {
    const [state, setState] = useState({ loading: true, events: [], error: null });

    useEffect(() => {
        if (!serialNumber) { setState({ loading: false, events: [], error: null }); return undefined; }
        setState({ loading: true, events: [], error: null });
        return firebase.firestore()
            .collection('terminals').doc(serialNumber)
            .collection('terminalHistory')
            .orderBy('at', 'desc')
            .limit(max)
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    events: snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
                    error: null,
                }),
                (err) => {
                    console.warn('[useTerminalHistory] listen failed:', err && err.message);
                    setState({ loading: false, events: [], error: err.message || 'Could not load history' });
                },
            );
    }, [serialNumber, max]);

    return [state.loading, state.events, state.error];
}

/**
 * The owner client id of each team on the Terminals page — the registry row
 * carries teamId and teamName but not the owner, and every admin team route
 * is /admin/clients/:clientId/teams/:teamId. One read per DISTINCT team,
 * cached for the page's life; a team that cannot be read simply has no link.
 * Returns { [teamId]: teamOwnerClientId }.
 */
export function useTeamOwners(teamIds) {
    const [owners, setOwners] = useState({});
    const wanted = [...new Set((teamIds || []).filter(Boolean))].sort().join(',');
    useEffect(() => {
        if (!wanted) return undefined;
        let cancelled = false;
        const missing = wanted.split(',').filter((id) => !(id in owners));
        if (missing.length === 0) return undefined;
        Promise.all(missing.map((teamId) => firebase.firestore().collection('teams').doc(teamId).get()
            .then((snap) => [teamId, (snap.exists && snap.data().teamOwnerClientId) || null])
            .catch(() => [teamId, null])))
            .then((pairs) => {
                if (cancelled) return;
                setOwners((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
            });
        return () => { cancelled = true; };
    }, [wanted, owners]);
    return owners;
}
